import { MapPin, X } from 'lucide-react';
import { type ComponentProps, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Card } from '@apps/web/components/ui/card';
import {
    AVAILABILITY_MAP_STYLES,
    CLIENT_MARKER_BACKGROUND,
    CLIENT_MARKER_COLOR,
} from '@apps/web/features/coverage/model/map_colors';
import { ClientOutreachPanel } from '@apps/web/features/coverage/ui/client_outreach_panel';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { type WorkforceMapPoint } from '@libraries/typescript/api_client';

export type PotentialClientPin = {
    readonly latitude: number;
    readonly longitude: number;
    readonly formattedAddress?: string;
    readonly clientId?: string;
    readonly clientName?: string;
};

const NEAREST_CAREGIVER_COUNT = 4;

export function MapSelectionCard({
    organizationId,
    nearbyCaregivers,
    radiusMiles,
    onRadiusChange,
    selectedShifts,
    onSelectedShiftsChange,
    onSelectedCaregiverIdsChange,
    availability,
    colorByCaregiver,
    plotted,
    onClear,
    onSelectCaregiver,
}: {
    readonly selectedShifts: ComponentProps<
        typeof ClientOutreachPanel
    >['selectedShifts'];
    readonly onSelectedShiftsChange: ComponentProps<
        typeof ClientOutreachPanel
    >['onSelectedShiftsChange'];
    readonly onSelectedCaregiverIdsChange: NonNullable<
        ComponentProps<
            typeof ClientOutreachPanel
        >['onSelectedCaregiverIdsChange']
    >;
    readonly availability: ComponentProps<
        typeof ClientOutreachPanel
    >['availability'];
    readonly organizationId: string | null;
    readonly nearbyCaregivers: ReadonlyArray<{
        readonly point: WorkforceMapPoint;
        readonly miles: number;
    }>;
    readonly radiusMiles: number;
    readonly onRadiusChange: (miles: number) => void;
    readonly colorByCaregiver: ReadonlyMap<string, string>;
    readonly plotted: PotentialClientPin | null;
    readonly onClear: () => void;
    readonly onSelectCaregiver: (point: WorkforceMapPoint) => void;
}) {
    const { formatName } = useFormatPersonName();
    const cardRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<{
        x: number;
        y: number;
        width: number;
        height: number;
    } | null>(null);
    const [size, setSize] = useState({ width: 320, height: 470 });
    const resize = (width: number, height: number) => {
        const parent = cardRef.current?.parentElement;
        if (!parent) {
            return;
        }
        const maxWidth = Math.max(0, parent.clientWidth - 24);
        const maxHeight = Math.max(0, parent.clientHeight - 24);
        setSize({
            width: Math.min(maxWidth, Math.max(Math.min(280, maxWidth), width)),
            height: Math.min(
                maxHeight,
                Math.max(Math.min(300, maxHeight), height),
            ),
        });
    };
    return (
        <Card
            ref={cardRef}
            role="region"
            aria-label="Client coverage panel"
            style={size}
            className="absolute bottom-3 left-3 z-10 max-h-[calc(100%_-_1.5rem)] max-w-[calc(100%_-_1.5rem)] gap-0 overflow-hidden py-0 shadow-lg"
        >
            {plotted ? (
                <div className="border-border flex min-h-0 flex-1 flex-col">
                    <div
                        className="flex shrink-0 items-center gap-2 py-2 pr-9 pl-3"
                        style={{ backgroundColor: CLIENT_MARKER_BACKGROUND }}
                    >
                        <MapPin
                            className="size-3.5 shrink-0 fill-current"
                            style={{ color: CLIENT_MARKER_COLOR }}
                            aria-hidden="true"
                        />
                        <span className="grow truncate text-xs font-medium">
                            {plotted.clientName ? (
                                <span className="block">
                                    {formatName(plotted.clientName)}
                                </span>
                            ) : null}
                            {plotted.formattedAddress ? (
                                <span className="block">
                                    {plotted.formattedAddress}
                                </span>
                            ) : null}
                        </span>
                        <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground absolute top-0 right-0 z-20 flex size-6 cursor-nesw-resize touch-none items-start justify-end p-1"
                            aria-label="Resize client panel"
                            title="Drag up or right to enlarge. Use arrow keys when focused."
                            onPointerDown={(event) => {
                                if (event.button !== 0) {
                                    return;
                                }
                                const bounds =
                                    cardRef.current?.getBoundingClientRect();
                                if (!bounds) {
                                    return;
                                }
                                event.preventDefault();
                                event.currentTarget.focus();
                                event.currentTarget.setPointerCapture(
                                    event.pointerId,
                                );
                                dragRef.current = {
                                    x: event.clientX,
                                    y: event.clientY,
                                    width: bounds.width,
                                    height: bounds.height,
                                };
                            }}
                            onPointerMove={(event) => {
                                const drag = dragRef.current;
                                if (drag) {
                                    resize(
                                        drag.width + event.clientX - drag.x,
                                        drag.height + drag.y - event.clientY,
                                    );
                                }
                            }}
                            onPointerUp={(event) => {
                                dragRef.current = null;
                                if (
                                    event.currentTarget.hasPointerCapture(
                                        event.pointerId,
                                    )
                                ) {
                                    event.currentTarget.releasePointerCapture(
                                        event.pointerId,
                                    );
                                }
                            }}
                            onPointerCancel={() => {
                                dragRef.current = null;
                            }}
                            onLostPointerCapture={() => {
                                dragRef.current = null;
                            }}
                            onKeyDown={(event) => {
                                const bounds =
                                    cardRef.current?.getBoundingClientRect();
                                if (!bounds) {
                                    return;
                                }
                                const step = event.shiftKey ? 80 : 24;
                                switch (event.key) {
                                    case 'ArrowRight':
                                        resize(
                                            bounds.width + step,
                                            bounds.height,
                                        );
                                        break;
                                    case 'ArrowLeft':
                                        resize(
                                            bounds.width - step,
                                            bounds.height,
                                        );
                                        break;
                                    case 'ArrowUp':
                                        resize(
                                            bounds.width,
                                            bounds.height + step,
                                        );
                                        break;
                                    case 'ArrowDown':
                                        resize(
                                            bounds.width,
                                            bounds.height - step,
                                        );
                                        break;
                                    default:
                                        return;
                                }
                                event.preventDefault();
                            }}
                        >
                            <svg
                                className="size-4"
                                viewBox="0 0 16 16"
                                fill="none"
                                aria-hidden="true"
                            >
                                <path
                                    d="M3 2L14 13M8 2L14 8M13 2L14 3"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-6 shrink-0"
                            aria-label="Remove searched client or address from map"
                            onClick={onClear}
                        >
                            <X className="size-3.5" />
                        </Button>
                    </div>
                    <div className="shrink-0 px-3 pt-2">
                        <div className="flex items-center justify-between text-xs">
                            <label htmlFor="coverage-radius">Radius</label>
                            <span>{radiusMiles} mi</span>
                        </div>
                        <input
                            id="coverage-radius"
                            type="range"
                            min={1}
                            max={30}
                            step={1}
                            value={radiusMiles}
                            onChange={(event) =>
                                onRadiusChange(Number(event.target.value))
                            }
                            aria-label="Radius"
                            className="accent-primary h-6 w-full"
                        />
                        <p className="text-muted-foreground w-full text-left text-xs">
                            {nearbyCaregivers.length} caregivers within{' '}
                            {radiusMiles} mi
                        </p>
                    </div>
                    {plotted.clientId && organizationId ? (
                        <ClientOutreachPanel
                            key={`${organizationId}:${plotted.clientId}`}
                            organizationId={organizationId}
                            clientId={plotted.clientId}
                            caregivers={nearbyCaregivers}
                            selectedShifts={selectedShifts}
                            onSelectedShiftsChange={onSelectedShiftsChange}
                            availability={availability}
                            onSelectedCaregiverIdsChange={
                                onSelectedCaregiverIdsChange
                            }
                        />
                    ) : nearbyCaregivers.length > 0 ? (
                        <div className="pb-2">
                            <p className="text-muted-foreground px-3 pt-2 pb-1 text-[11px] font-medium">
                                Nearest mapped caregivers
                            </p>
                            {nearbyCaregivers
                                .slice(0, NEAREST_CAREGIVER_COUNT)
                                .map(({ point, miles }) => (
                                    <Button
                                        key={point.caregiver_id}
                                        variant="ghost"
                                        type="button"
                                        className="h-auto w-full justify-start gap-2 rounded-none px-3 py-1.5 text-left font-normal"
                                        onClick={() => onSelectCaregiver(point)}
                                    >
                                        <span
                                            className="size-2 shrink-0 rounded-full"
                                            style={{
                                                backgroundColor:
                                                    colorByCaregiver.get(
                                                        point.caregiver_id,
                                                    ),
                                            }}
                                            aria-hidden="true"
                                        />
                                        <span className="min-w-0 grow text-xs">
                                            <span className="block truncate">
                                                {formatName(point.name)}
                                            </span>
                                            <span className="text-muted-foreground block text-[11px]">
                                                {
                                                    AVAILABILITY_MAP_STYLES[
                                                        point.availability_status ??
                                                            'unknown'
                                                    ].label
                                                }
                                            </span>
                                        </span>
                                        <span className="text-muted-foreground shrink-0 text-[11px] tabular-nums">
                                            {miles.toFixed(1)} mi
                                        </span>
                                    </Button>
                                ))}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </Card>
    );
}
